import { TimePeriod } from "@/lib/dateUtils";

export const platformSettingsOptions = () => ({
    queryKey: ["platformSettings"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});


export const supplierJobProcessesOptions = (supplierId: string) => ({
    queryKey: ["supplierJobProcesses", supplierId] as const,
    staleTime: 1 * 60 * 1000, // 1 minute - job status changes frequently
});

export const globalJudgePromptHistoryOptions = (clientId: string) => ({
    queryKey: ["globalJudgePrompts", clientId] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes
});

export const globalQuestionnairePromptHistoryOptions = (clientId: string) => ({
    queryKey: ["globalQuestionnairePrompts", clientId] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes
});

export const orgInfoForQuestionnaireOptions = () => ({
    queryKey: ["orgInfoForQuestionnaire"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const portfolioSummaryOptions = () => ({
    queryKey: ["portfolioSummary"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const latestPortfolioSummaryProcessOptions = () => ({
    queryKey: ["latestPortfolioSummaryProcess"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
});

export const documentsByIdsOptions = (documentIds: string[]) => ({
    queryKey: ["documentsByIds", ...documentIds.sort()] as const,
    staleTime: 10 * 60 * 1000, // 10 minutes - document names rarely change
});

export const someClientsExistOptions = () => ({
    queryKey: ["someClientsExist"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const creditsOptions = () => ({
    queryKey: ["credits"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const totalCreditsUsedOptions = () => ({
    queryKey: ["totalCreditsUsed"] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - credits usage changes more frequently
});

export const availableCreditsOptions = () => ({
    queryKey: ["availableCredits"] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - available credits can change when new credits are added
});

export const orchestrationApprovalsOptions = () => ({
    queryKey: ["orchestrationApprovals"] as const,
    staleTime: 1 * 60 * 1000, // 1 minute - approvals need fresh data
});

export const orchestrationMessagesOptions = () => ({
    queryKey: ["orchestrationMessages"] as const,
    staleTime: 1 * 60 * 1000, // 1 minute - messages need fresh data
});

export const ongoingJobsOptions = () => ({
    queryKey: ["ongoingJobs"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
});

export const jobsByDateRangeOptions = (
    clientId: string | undefined,
    period: TimePeriod,
) => ({
    queryKey: ["jobsByDateRange", clientId, period] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - job creation data changes frequently
});

export const jobsByPreviousPeriodOptions = (
    clientId: string | undefined,
    period: TimePeriod,
) => ({
    queryKey: ["jobsByPreviousPeriod", clientId, period] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - job creation data changes frequently
});

export const ongoingJobsForClientOptions = (clientId: string) => ({
    queryKey: ["ongoingJobsForClient", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
});

export const rawDocumentOptions = (documentId: string) => ({
    queryKey: ["rawDocument", documentId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allClientsOptions = () => ({
    queryKey: ["allClients"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allProductionClientsOptions = () => ({
    queryKey: ["allProductionClients"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientOptions = (clientId: string) => ({
    queryKey: ["client", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allClientMetadataOptions = () => ({
    queryKey: ["allClientMetadata"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientMetadataOptions = (clientId: string) => ({
    queryKey: ["clientMetadata", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allClientFinancialTypesOptions = () => ({
    queryKey: ["allClientFinancialTypes"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientFinancialTypesOptions = (clientId: string) => ({
    queryKey: ["clientFinancialTypes", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const rawDocumentMetadataOptions = (documentId: string) => ({
    queryKey: ["rawDocumentMetadata", documentId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const richChunkOptions = (chunkId: string) => ({
    queryKey: ["richChunk", chunkId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const researchRequestOptions = (requestId: string) => ({
    queryKey: ["researchRequest", requestId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const researchAnswerOptions = (answerId: string) => ({
    queryKey: ["researchAnswer", answerId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const thirdPartyScanOptions = (scanId: string) => ({
    queryKey: ["thirdPartyScan", scanId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientContextOptions = (clientId: string) => ({
    queryKey: ["clientContext", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allDocumentsOptions = (clientId?: string) => ({
    queryKey: ["allDocuments", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allThirdPartyScansOptions = (clientId?: string) => ({
    queryKey: ["allThirdPartyScans", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allResearchRequestsOptions = (clientId?: string) => ({
    queryKey: ["allResearchRequests", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientResearchAnswersOptions = (clientId: string) => ({
    queryKey: ["clientResearchAnswers", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientDocumentCountOptions = (clientId: string) => ({
    queryKey: ["clientDocumentCount", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientActionItemsCountOptions = (clientId: string) => ({
    queryKey: ["clientActionItemsCount", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientActionItemsOptions = (clientId: string) => ({
    queryKey: ["clientActionItems", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientResearchAnswerCountOptions = (clientId: string) => ({
    queryKey: ["clientResearchAnswerCount", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const isClientCreditAssetOptions = (clientId: string) => ({
    queryKey: ["isClientCreditAsset", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const simulatedLossOptions = (clientId: string) => ({
    queryKey: ["simulatedLoss", clientId],
    staleTime: 1000 * 60 * 5, // 5 minutes
});

export const userByIdOptions = (userId: string) => ({
    queryKey: ["userById", userId] as const,
    staleTime: 30 * 60 * 1000, // 30 minutes
});

export const userOptions = () => ({
    queryKey: ["user"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const richChunkCountOptions = (documentId: string) => ({
    queryKey: ["richChunkCount", documentId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientChatSessionsOptions = () => ({
    queryKey: ["clientChatSessions"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Third Party query options
export const allThirdPartiesOptions = () => ({
    queryKey: ["third-parties", "all"],
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const thirdPartyOptions = (thirdPartyId: string) => ({
    queryKey: ["third-party", thirdPartyId],
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Questionnaire related query options
export const allQuestionnairesOptions = () => ({
    queryKey: ["allQuestionnaires"] as const,
    refetchOnWindowFocus: true,
    refetchOnMount: "always" as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allQuestionnaireMetadataOptions = () => ({
    queryKey: ["allQuestionnaireMetadata"] as const,
    refetchOnWindowFocus: true,
    refetchOnMount: "always" as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// New query key options for questionnaire details
export const questionnaireOptions = (questionnaireId: string) => ({
    queryKey: ["questionnaire", questionnaireId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const questionnaireMetadataOptions = (questionnaireId: string) => ({
    queryKey: ["questionnaireMetadata", questionnaireId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allocatableGroupsOptions = () => ({
    queryKey: ["allocatableGroups"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const questionsByQuestionnaireOptions = (questionnaireId: string) => ({
    queryKey: ["questionsByQuestionnaire", questionnaireId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
});

export const answersByQuestionnaireOptions = (questionnaireId: string) => ({
    queryKey: ["answersByQuestionnaire", questionnaireId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
});

export const answerStatusOptions = (questionId: string) => ({
    queryKey: ["answerStatus", questionId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const questionAnswerAllocationOptions = (questionId: string) => ({
    queryKey: ["questionAnswerAllocation", questionId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// New query key options for bulk operations
export const bulkAnswerStatusesOptions = (questionIds: string[]) => ({
    queryKey: ["bulkAnswerStatuses", questionIds] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const bulkQuestionAnswerAllocationsOptions = (
    questionIds: string[],
) => ({
    queryKey: ["bulkQuestionAnswerAllocations", questionIds] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const thirdPartiesByTypeOptions = (types: string[]) => ({
    queryKey: ["third-parties-by-type", types],
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const customerRelationshipsOptions = (parentClientId: string) => ({
    queryKey: ["customerRelationships", parentClientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Client Relationship query options
export const clientRelationshipsByParentOptions = (
    parentId: string,
    allowedTypes: readonly string[],
) => ({
    queryKey: ["clientRelationshipsByParent", parentId, allowedTypes] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientRelationshipByParentChildOptions = (
    parentId: string,
    childId: string,
) => ({
    queryKey: ["clientRelationshipByParentChild", parentId, childId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allClientRelationshipsOptions = () => ({
    queryKey: ["allClientRelationships"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientsByIdsOptions = (clientIds: string[]) => ({
    queryKey: ["clientsByIds", clientIds] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const clientsByEntityIdsOptions = (entityIds: string[]) => ({
    queryKey: ["clientsByEntityIds", entityIds.sort()] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes - client names don't change often
});

export const clientRelationshipsByChildOptions = (childId: string) => ({
    queryKey: ["clientRelationshipsByChild", childId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const parentClientsOptions = (childId: string) => ({
    queryKey: ["parentClients", childId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const thirdPartyCountOptions = (parentId: string) => ({
    queryKey: ["thirdPartyCount", parentId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const thirdPartiesExistByParentOptions = (parentId: string) => ({
    queryKey: ["thirdPartiesExistByParent", parentId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const rawDocumentTagOptions = (documentId: string) => ({
    queryKey: ["rawDocumentTag", documentId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const questionSetOptions = (questionSetId: string) => ({
    queryKey: ["questionSet", questionSetId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// New query key options for batch question answering
export const latestBatchQuestionAnsweringProcessOptions = (
    questionnaireId: string,
) => ({
    queryKey: ["latestBatchQuestionAnsweringProcess", questionnaireId] as const,
    staleTime: 5 * 1000, // 5 seconds
    refetchOnWindowFocus: true,
});

export const batchQuestionAnsweringProgressOptions = (
    questionnaireId: string,
) => ({
    queryKey: ["batchQuestionAnsweringProgress", questionnaireId] as const,
    staleTime: 10 * 1000, // 10 seconds - keep data fresh as it's based on subscription updates
});

export const answerReasoningOptions = (questionId: string) => ({
    queryKey: ["answerReasoning", questionId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const bulkAnswerReasoningOptions = (questionIds: string[]) => ({
    queryKey: ["bulkAnswerReasoning", questionIds] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Cybersecurity process and information query keys
export const informationOnCybersecurityProcessOptions = (clientId: string) => ({
    queryKey: ["informationOnCybersecurityProcess", clientId] as const,
    staleTime: 5 * 1000, // 5 seconds for process status
    refetchOnWindowFocus: true,
});

export const providesCybersecurityProcessOptions = (clientId: string) => ({
    queryKey: ["providesCybersecurityProcess", clientId] as const,
    staleTime: 5 * 1000, // 5 seconds for process status
    refetchOnWindowFocus: true,
});

export const cybersecurityInformationOptions = (clientId: string) => ({
    queryKey: ["cybersecurityInformation", clientId] as const,
    staleTime: 10 * 1000, // 10 seconds for real-time data
    refetchOnMount: "always" as const, // Force refetch on mount
    refetchOnWindowFocus: true,
});

export const providesCybersecurityOptions = (clientId: string) => ({
    queryKey: ["providesCybersecurity", clientId] as const,
    staleTime: 10 * 1000, // 10 seconds for real-time data
    refetchOnMount: "always" as const, // Force refetch on mount
    refetchOnWindowFocus: true,
});

export const totalRichChunkCountByClientOptions = (clientId: string) => ({
    queryKey: ["totalRichChunkCount", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const supplierIdentificationSettingsOptions = () => ({
    queryKey: ["supplierIdentificationSettings"],
    staleTime: 1000 * 60 * 5, // 5 minutes
});

export const clientDetailsOptions = (clientId: string | null | undefined) => ({
    queryKey: ["clientDetails", clientId],
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!clientId, // Important: only enable if clientId is present
});

// Supplier Identification Process Query Keys
export const supplierIdentificationProcessesOptions = (clientId?: string) => ({
    queryKey: ["supplierIdentificationProcesses", clientId] as const,
    staleTime: 10 * 1000, // 10 seconds, to keep active processes updated
    refetchOnWindowFocus: true,
});

export const supplierIdentificationReasoningOptions = (documentId: string) => ({
    queryKey: ["supplierIdentificationReasoning", documentId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
});

// Raw documents for supplier identification query options
export const rawDocumentsForSupplierIdentificationOptions = () => ({
    queryKey: ["documents", "forSupplierIdentification"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Raw documents for questionnaire processing query options
export const rawDocumentsForQuestionnaireOptions = () => ({
    queryKey: ["documents", "forQuestionnaire"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Document to questionnaire process query options
export const documentToQuestionnaireProcessesOptions = () => ({
    queryKey: ["documentToQuestionnaireProcesses"] as const,
    staleTime: 10 * 1000, // 10 seconds, to keep active processes updated
});

// Raw document metadata IDs query options
export const rawDocumentMetadataIdsOptions = (documentIds: string[]) => ({
    queryKey: ["documentMetadata", "ids", documentIds] as const,
    staleTime: 60 * 1000, // 1 minute to reflect metadata changes faster
});

export const hasInformationForLossCalcOptions = (clientId: string) => ({
    queryKey: ["hasInformationForLossCalc", clientId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Workflow Configuration options
export const workflowConfigsOptions = (filters?: {
    pageSize?: number;
    pageToken?: string;
    isActive?: boolean;
    name?: string;
}) => ({
    queryKey: ["workflowConfigs", filters] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const workflowConfigOptions = (configId: string) => ({
    queryKey: ["workflowConfig", configId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const latestWorkflowConfigOptions = (configName: string) => ({
    queryKey: ["latestWorkflowConfig", configName] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter cache for latest version
});

export const workflowConfigVersionsOptions = (
    configId: string,
    filters?: { pageSize?: number; pageToken?: string },
) => ({
    queryKey: ["workflowConfigVersions", configId, filters] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - versions don't change frequently
});

export const latestWorkflowConfigurationByTypeOptions = (
    workflowType: string,
) => ({
    queryKey: ["workflowConfiguration", "latest", workflowType] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const workflowConfigurationByIdOptions = (
    id: string | undefined,
) => ({
    queryKey: ["workflowConfiguration", id] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Citation existence check
export const citationExistsOptions = (type: string, id: string) => ({
    queryKey: ["citationExists", type, id] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const questionnaireStylePreferencesOptions = (
    questionnaireId: string,
) => ({
    queryKey: ["questionnaireStylePreferences", questionnaireId] as const,
    staleTime: 10 * 1000, // 10 seconds - shorter staleTime for user-editable data
});

export const userAllocationSettingsOptions = () => ({
    queryKey: ["userAllocationSettings"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const allGroupsOptions = () => ({
    queryKey: ["allGroups"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const personasOptions = () => ({
    queryKey: ["personas"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const groupMembersOptions = (groupId: string) => ({
    queryKey: ["groupMembers", groupId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const userAllocationOptions = (
    objectId: string,
    allocationType: string,
) => ({
    queryKey: ["userAllocation", objectId, allocationType] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const userAllocationsByClientOptions = (
    clientId: string,
    allocationType: string,
) => ({
    queryKey: ["userAllocationsByClient", clientId, allocationType] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const personaByIdOptions = (personaId: string) => ({
    queryKey: ["persona", personaId],
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
});

// Integration-related query options
export const userIntegrationsOptions = (
    provider?: string,
    status?: string,
) => ({
    queryKey: ["userIntegrations", provider, status] as const,
    staleTime: 30 * 1000, // 30 seconds (integration status can change frequently)
    refetchOnWindowFocus: true,
    refetchOnMount: true,
});

export const integrationDetailsOptions = (integrationId: string) => ({
    queryKey: ["integrationDetails", integrationId] as const,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
});

// User management options - scoped by customer's ontology RID
export const usersOptions = () => ({
    queryKey: ["users"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const userDetailsOptions = (email: string) => ({
    queryKey: ["userDetails", email] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const userAuditLogsOptions = (email: string) => ({
    queryKey: ["userAuditLogs", email] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const validateMagicLinkTokenOptions = (token: string | null) => ({
    queryKey: ["validateMagicLinkToken", token] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!token,
});

// Calendar event query options
export const calendarEventOptions = (eventId: string) => ({
    queryKey: ["calendarEvent", eventId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const calendarEventAttendeesOptions = (eventId: string) => ({
    queryKey: ["calendarEventAttendees", eventId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const calendarEventsOptions = (
    startDate?: string,
    endDate?: string,
    pageSize?: number,
) => ({
    queryKey: ["calendarEvents", startDate, endDate, pageSize] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent updates for activity sidebar)
});

export const calendarEventAttendeesBatchOptions = (eventIds: string[]) => ({
    queryKey: ["calendarEventAttendeesBatch", eventIds] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const calendarEventNotesOptions = (eventId: string) => ({
    queryKey: ["calendarEventNotes", eventId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
});

// Workflow configuration options
export const workflowTagsOptions = () => ({
    queryKey: ["workflowTags"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const workflowCriticalityLevelsOptions = () => ({
    queryKey: ["workflowCriticalityLevels"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const workflowCriticalityAssessmentsOptions = () => ({
    queryKey: ["workflowCriticalityAssessments"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const workflowQuestionSetsOptions = () => ({
    queryKey: ["workflowQuestionSets"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const proposedSuppliersOptions = (clientId?: string) => ({
    queryKey: clientId
        ? (["proposedSuppliers", clientId] as const)
        : (["proposedSuppliers"] as const),
    staleTime: 30 * 1000, // 30 seconds - more frequent updates for pending suppliers
    refetchOnWindowFocus: true,
    refetchOnMount: true,
});

// Supplier approval status query options
export const supplierApprovalStatusOptions = (relationshipId: string) => ({
    queryKey: ["supplierApprovalStatus", relationshipId] as const,
    staleTime: 30 * 1000, // 30 seconds - approval status can change frequently
});

export const supplierApprovalStatusBatchOptions = (
    relationshipIds: string[],
) => ({
    queryKey: ["supplierApprovalStatusBatch", relationshipIds] as const,
    staleTime: 30 * 1000, // 30 seconds - approval status can change frequently
});

export const supplierApprovalHistoryOptions = (relationshipId: string) => ({
    queryKey: ["supplierApprovalHistory", relationshipId] as const,
    staleTime: 60 * 1000, // 1 minute - history doesn't change as frequently
});

// Production descriptor query options
export const allProductionDescriptorsOptions = () => ({
    queryKey: ["allProductionDescriptors"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes - descriptors don't change frequently
});

export const productionDescriptorOptions = (descriptorId: string) => ({
    queryKey: ["productionDescriptor", descriptorId] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Production client descriptor query options
export const clientDescriptorsOptions = (clientId: string) => ({
    queryKey: ["clientDescriptors", clientId] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - client tags may change frequently
});

export const clientDescriptorsBatchOptions = (clientIds: string[]) => ({
    queryKey: ["clientDescriptorsBatch", clientIds] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - client tags may change frequently
});

// Internal question sets query options
export const allInternalQuestionSetsOptions = () => ({
    queryKey: ["allInternalQuestionSets"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Question sets query options (for correspondence)
export const allQuestionSetsOptions = () => ({
    queryKey: ["allQuestionSets"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// User prompts query options
export const userPromptsOptions = (type: string) => ({
    queryKey: ["userPrompts", type] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Correspondence query options
export const correspondencesByClientOptions = (clientId: string) => ({
    queryKey: ["correspondences", "byClient", clientId] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - correspondence data changes frequently
    refetchOnMount: "always" as const, // Always refetch when navigating back to list
});

export const correspondenceDetailOptions = (correspondenceId: string) => ({
    queryKey: ["correspondence", correspondenceId] as const,
    staleTime: 1 * 60 * 1000, // 1 minute - correspondence detail may change
});

// Document content query options (for blob-proxied content)
export const documentContentOptions = (documentId: string) => ({
    queryKey: ["documentContent", documentId] as const,
    staleTime: 10 * 60 * 1000, // 10 minutes - document content rarely changes
});

export const documentMarkdownOptions = (documentId: string) => ({
    queryKey: ["documentMarkdown", documentId] as const,
    staleTime: 10 * 60 * 1000, // 10 minutes - markdown content rarely changes
});

// Chat conversations query options
export const chatConversationsOptions = (entityId: string) => ({
    queryKey: ["chatConversations", entityId] as const,
    staleTime: 2 * 60 * 1000, // 2 minutes - conversations list changes when new chats are created
});

// Inbound Question allocation query options
export const questionAllocationsOptions = (questionId: string) => ({
    queryKey: ["questionAllocations", questionId, "InboundQuestion"] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

export const questionAllocationsByQuestionnaireOptions = (
    questionnaireId: string,
) => ({
    queryKey: [
        "questionAllocationsByQuestionnaire",
        questionnaireId,
        "InboundQuestion",
    ] as const,
    staleTime: 5 * 60 * 1000, // 5 minutes
});

// Entity Candidates query options (supplier identification workflow)
export const entityCandidatesOptions = (firstPartyId?: string) => ({
    queryKey: firstPartyId
        ? (["entityCandidates", firstPartyId] as const)
        : (["entityCandidates"] as const),
    staleTime: 30 * 1000, // 30 seconds - candidates change during review workflow
    refetchOnWindowFocus: true,
    refetchOnMount: true,
});

